import type { GenericArrayOfObjects, INormalizedResponse } from '@/interfaces/api';
import type { INovaCompany } from '@/interfaces/nova';
import { notify } from '@kyvg/vue3-notification';

export const EMAIL_SEPARATION_REGEX = /[,;]/gu;

const notificationConfigs = {
  success: { icon: 'mdi-check-circle-outline', appClasses: 'success' },
  error: { icon: 'mdi-alert-circle-outline', appClasses: 'error' },
  warning: { icon: 'mdi-alert-circle-outline', appClasses: 'warning' },
  info: { icon: 'mdi-information-outline', appClasses: 'info' }
};

export function concatOptionalField(field1: string, field2?: string): string {
  return field1 + (field2 ? ` ${field2}` : '');
}

export function getProperty<TValue = unknown>(obj: object, propertyName: string): TValue {
  return (obj as Record<string, TValue>)[propertyName] as TValue;
}

export function deleteProperty(obj: object, propertyName: string): void {
  Reflect.deleteProperty(obj, propertyName);
}

export function minutesSinceMidnightToClock(minutes: number, leadingZeroHour = false): string {
  const wholeHours = Math.floor(minutes / 60);
  // Instead of 24:00, we need to format to 23:59 so it fits the expected 'end of day' time in Nova.
  // Padded clocks are joined into an ISO datetime, where T24:00 is valid and means midnight of the next day.
  if (wholeHours === 24 && !leadingZeroHour) {
    return '23:59';
  }
  const hours = leadingZeroHour ? leftPadString(wholeHours) : wholeHours;
  return `${hours}:${leftPadString(minutes % 60)}`;
}

export function leftPadString(str: string | number, charactersToPad = 2) {
  return str.toString().padStart(charactersToPad, '0');
}

export function cleanObjectArr<TObject extends object>(objectArr: TObject) {
  Object.keys(objectArr).forEach(key => {
    if (!getProperty(objectArr, key)) {
      deleteProperty(objectArr, key);
    }
  });
  return objectArr;
}

function getNotificationConfig(type: string) {
  return getProperty<(typeof notificationConfigs)[keyof typeof notificationConfigs] | undefined>(
    notificationConfigs,
    type
  );
}

type SystemNotifyOptions = {
  duration?: number;
  clean?: boolean;
  actionText?: string | null;
  actionFn?: (() => void) | null;
};

type SystemNotifyParams = {
  message: string;
  type?: string;
  title?: string;
  options?: SystemNotifyOptions;
};
export function systemNotify({ message, type = 'success', options = {} }: SystemNotifyParams) {
  const resolvedOptions: SystemNotifyOptions = {
    duration: 5000,
    clean: false,
    actionText: null,
    actionFn: null,
    ...options
  };
  notify({
    title: ucFirst(type),
    text: message,
    type,
    duration: resolvedOptions.duration,
    data: { ...getNotificationConfig(type), ...resolvedOptions },
    clean: resolvedOptions.clean
  });
}

export function ucFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function hasErrors(response: Pick<INormalizedResponse, 'errors'>): boolean {
  return Boolean(response.errors?.length);
}

export function flashErrors(errors: string[]) {
  errors.forEach(error => {
    systemNotify({ message: error, type: 'error' });
  });
}

export function flashErrorsIfExist(response: Pick<INormalizedResponse, 'errors'>): boolean {
  if (hasErrors(response)) {
    flashErrors(response.errors as string[]);
  }

  return hasErrors(response);
}

export function successRes(status: number) {
  return status >= 200 && status < 300;
}

export function formatPhone(phone: string): string {
  let formattedPhone = String(phone).replace(/[^a-zA-Z0-9]/gu, '');
  if (formattedPhone.charAt(0) === '1') {
    formattedPhone = formattedPhone.substring(1);
  }
  return formattedPhone;
}

export function keyCompaniesByName(companyArr: INovaCompany[]) {
  const companiesKeyedByName: Record<string, INovaCompany> = {};
  companyArr.forEach(company => {
    companiesKeyedByName[company.name] = company;
  });
  return companiesKeyedByName;
}

export function cleanWhiteSpace(str = '', replacement = ' '): string {
  if (!str) {
    return '';
  }
  return str.replace(/\s\s+/gu, replacement).trim();
}

export function makeUniqueObjectArray<TItem extends object>(arr: TItem[]): TItem[] {
  return arr.filter((value, index) => {
    const objectVal = JSON.stringify(value);
    return (
      index ===
      arr.findIndex(obj => {
        return JSON.stringify(obj) === objectVal;
      })
    );
  });
}

export async function sleep(milliseconds = 0) {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });
}

export function emailStrToFormattedArray(emailStr: string): string[] {
  return emailStr
    .split(EMAIL_SEPARATION_REGEX)
    .map(email => email.replace(/\s+/gu, '').toLowerCase())
    .filter(email => email.length > 0);
}

export function emailToFormattedArray(email: string | string[]): string[] {
  if (Array.isArray(email)) {
    return emailStrToFormattedArray(email.join(','));
  }
  return email ? emailStrToFormattedArray(email) : [];
}

export function objHasProperty(obj: object, property: string) {
  return Object.prototype.hasOwnProperty.call(obj, property);
}
